<template>
  <v-container class="mt-5 mb-15 px-10" fluid>
    <PageHeader>
      <template #header>
        <img
          v-if="pluginLogo"
          :src="pluginLogo"
          :alt="`${pluginName} logo`"
          height="32"
        >
        {{ installed ? 'Update ' : 'Install' }} {{ pluginName }} Plugin
      </template>
      <template #subheader>
        Please configure the following in order to install the {{ pluginName }} plugin.
      </template>
    </PageHeader>

    <validation-observer ref="pluginForm">
      <div class="section-header">
        Configure Fields from File
      </div>
      <div class="mt-8">
        <CustomDropdown
          v-model="fieldMapping[customFieldMappingKey]"
          :header="customFieldMappingKey + ' - Custom Field'"
          :items="customFieldOptions"
          autocomplete
          hint="The CUSTOMER_ID field in the file will be mapped to this unique custom field."
        />
      </div>

      <hr class="mt-7">

      <div class="section-header">
        Automated Imports
      </div>
      <div class="mt-8">
        <v-btn
          class="custom-button custom-button--blue px-13"
          height="34px"
          depressed
          :block="$vuetify.breakpoint.xsOnly"
          @click="addImportConfiguration(null)"
        >
          Add Import Configuration
        </v-btn>
      </div>

      <div v-for="(form, index) in importConfigurations" :key="index" class="mt-4">
        <v-expansion-panels v-model="form.isShowing">
          <v-expansion-panel>
            <v-expansion-panel-header v-if="form.name">
              {{ form.name }}
            </v-expansion-panel-header>
            <v-expansion-panel-header v-else>
              Configuration #{{ index + 1 }}
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <AutomatedImportForm
                :form="form"
                :file-extension-options="fileExtensionOptions"
                @clone-configuration="cloneImportConfiguration(index)"
                @remove-configuration="removeImportConfiguration(index)"
                @update-value="(key, value) => updateImportValue(index, key, value)"
              />
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </div>

      <v-row class="mt-12">
        <v-col cols="12" class="d-flex justify-end">
          <v-btn
            v-if="installed"
            class="custom-button custom-button--red-text px-8 mr-4"
            height="34px"
            depressed
            @click="deactivateDialog = true;"
          >
            Deactivate
          </v-btn>
          <v-btn
            class="custom-button custom-button--blue px-13"
            height="34px"
            depressed
            :block="$vuetify.breakpoint.xsOnly"
            @click="installPlugin(importConfigurations, false)"
          >
            {{ installed ? "Configure" : "Install" }}
          </v-btn>
        </v-col>
      </v-row>
    </validation-observer>

    <v-dialog v-model="deleteImportDialog" max-width="360px" @click:outside="deleteImportId = null">
      <DeleteConfirmationDialog
        v-if="deleteImportDialog"
        header="Delete configuration"
        subheader="Are you sure you want to delete this import configuration? All unsaved changes will be removed."
        @dismiss="
          deleteImportDialog = false;
          deleteImportId = null;
        "
        @delete="deleteImportConfig"
      />
    </v-dialog>

    <DeactivateConfirmDialog
      v-if="deactivateDialog"
      :id="id"
      :plugin="pluginData"
      @dismiss="deactivateDialog = false;"
    />
  </v-container>
</template>

<script>
import PageHeader from "@/sharedComponents/PageHeader";
import AutomatedImportForm from "@/views/Plugins/components/AutomatedImportForm";
import CustomDropdown from "@/sharedComponents/CustomDropdown";
import { v4 as uuidv4 } from "uuid";
import DeleteConfirmationDialog from "@/sharedComponents/DeleteConfirmationDialog";
import DeactivateConfirmDialog from "./components/DeactivateConfirmDialog";
import importExportMixin from "@/utils/import-export-mixin";

export default {
  name: "WebMDSharedPlugin",
  components: {
    AutomatedImportForm,
    CustomDropdown,
    PageHeader,
    DeleteConfirmationDialog,
    DeactivateConfirmDialog,
  },
  mixins: [importExportMixin],
  props: {
    id: {
      default: () => null,
      type: [Number, String],
    },
    installed: {
      default: false,
      type: Boolean,
    },
  },
  data: () => ({
    deleteImportDialog: null,
    deleteImportId: null,
    deactivateDialog: false,
    importConfigurations: [],
    pluginData: null,
    pluginLogo: null,
    pluginName: null,
    importType: null,
    componentName: null,
    fileExtensionOptions: [
      { id: 'txt', text: 'Pipe-Delimited Text File (.txt)' },
    ],
    fieldMapping: [],
  }),
  computed: {
    customFieldOptions() {
      const customFields = this.$store.getters['user/customFields']?.contact;

      if (!customFields) {
        return [];
      }

      return [
        {
          label: 'Automatically create a custom field',
          value: null,
        },
        ...customFields.map((field) => {
          return {
            label: field.name,
            value: field.fieldName,
          };
        })
      ];
    },
    customFieldMappingKey() {
      switch (this.componentName) {
        case 'AddPluginMDedge':
          return 'MDedge ID';
        case 'AddPluginMedscape':
          return 'Medscape ID';
        case 'AddPluginWebMD':
          return 'WebMD ID';
        default:
          return '';
      }
    },
  },
  created() {
    if (this.installed) {
      this.loadAccountPlugin();
    } else {
      this.getPluginDataById();
    }
  },
  methods: {
    async getPluginDataById() {
      const resp = await this.$rest.plugins.getPluginDataById(this.id);
      if (!resp.data) {
        return;
      }
      this.pluginLogo = require(`@/assets/plugins/${resp?.data?.image}`);
      this.pluginName = resp?.data?.name;

      this.componentName = resp?.data?.componentName;
      this.importType = this.getSpecificPluginImportType();
    },
    async loadAccountPlugin() {
      const resp = await this.$rest.plugins.get_resource(this.id, {
        includeCredentials: true,
      });
      if (!resp.data) {
        return;
      }

      const credentials = resp.data.pluginConfiguration?.credentials;
      this.pluginData = resp.data;
      this.pluginLogo = require(`@/assets/plugins/${resp?.data?.plugin?.image}`);
      this.pluginName = resp?.data?.plugin?.name;

      this.componentName = resp?.data?.plugin?.componentName;
      this.importType = this.getSpecificPluginImportType();
      this.fieldMapping = this.pluginData?.pluginConfiguration?.fieldMapping ?? [];

      const configMapFunc = config => {
        const configCredentials = credentials[config.key] ?? null;

        if(config?.frequency?.time) {
          const [hour, minute] = config.frequency.time.split(":");
          if (+hour >= 12) {
            config.frequency.ampm = "PM"
            config.frequency.hour = +hour > 12 ? +hour - 12 : 12;
          }
          else {
            config.frequency.ampm = "AM"
            config.frequency.hour = (+hour === 0) ? 12 : hour;
          }
          config.frequency.minute = minute;

          // Remove this config in case the frequency changes
          delete config.frequency.nextRunDate;
        }

        return {
          ...config,
          credentials: configCredentials,
        };
      };

      if (resp.data.pluginConfiguration?.importConfigurations) {
        const importConfigurations = resp.data.pluginConfiguration?.importConfigurations.map(configMapFunc);
        importConfigurations.forEach(config => {
          this.addImportConfiguration(config);
        });
      }
    },
    getSpecificPluginImportType() {
      switch (this.componentName) {
        case 'AddPluginMDedge':
          return 'mdedge_action';
        case 'AddPluginMedscape':
          return 'medscape_action';
        case 'AddPluginWebMD':
          return 'webmd_action';
        default:
          return null;
      }
    },
    addImportConfiguration(existingConfig) {
      this.importConfigurations.push({
        connectionType: existingConfig?.connectionType || null,
        directory: existingConfig?.directory || '/',
        archiveDirectory: existingConfig?.archiveDirectory || '',
        fileExtension: existingConfig?.fileExtension || this.fileExtensionOptions[0]?.id,
        credentials: {
          host: existingConfig?.credentials?.host || null,
          username: existingConfig?.credentials?.username || null,
          password: existingConfig?.credentials?.password || null,
          port: existingConfig?.credentials?.port || null,
          privateKey: existingConfig?.credentials?.privateKey || null,
          aws_region: existingConfig?.credentials?.aws_region || null,
          aws_access_id: existingConfig?.credentials?.aws_access_id || null,
          aws_secret_key: existingConfig?.credentials?.aws_secret_key || null,
          aws_bucket: existingConfig?.credentials?.aws_bucket || null,
        },
        frequency: existingConfig?.frequency || {
          type: null,
          ampm: (new Date()).getHours() >= 12 ? 'PM' : 'AM',
          hour: (new Date()).getHours(),
          minute: '00',
          timezone: 'America/New_York',
          date: [],
        },
        importType: this.importType,
        importOptions: existingConfig?.importOptions ? {...existingConfig?.importOptions} : { choice: null },
        name: existingConfig?.name || '',
        notificationRecipients: existingConfig?.notificationRecipients || '',
        key: existingConfig?.key || uuidv4(),
        isShowing: false,
        history: existingConfig?.history ? [...existingConfig?.history] : [],
        isExisting: !!existingConfig?.key,
      });
    },
    cloneImportConfiguration(index) {
      const existingConfig = this.importConfigurations[index] ?? null;
      this.addImportConfiguration({
        ...existingConfig,
        name: 'Clone of ' + existingConfig?.name || '',
        key: null,
        history: null,
      });
    },
    removeImportConfiguration(index) {
      this.deleteImportDialog = true;
      this.deleteImportId = index;
    },
    async deleteImportConfig() {
      if (this.installed) {
        const modifiedImportConfig = this.importConfigurations.slice();
        modifiedImportConfig.splice(this.deleteImportId, 1);
        await this.installPlugin(modifiedImportConfig, true);

        this.importConfigurations.splice(this.deleteImportId, 1);

        this.$store.commit('snackbar/showMessage', {
          content: 'Import Configuration Deleted',
          color: 'success',
        });
      } else {
        this.importConfigurations.splice(this.deleteImportId, 1);
      }

      this.deleteImportDialog = null;
      this.deleteImportId = null;
    },
    updateImportValue(index, key, value) {
      const configuration = this.importConfigurations[index];

      if (key.includes('.')) {
        const [parentKey, childKey] = key.split('.');

        configuration[parentKey][childKey] = value;
        const update = {};
        update[childKey] = value;
        this.$set(configuration, parentKey, {
          ...configuration[parentKey],
          ...update
        });
      } else {
        configuration[key] = value;
        this.$set(configuration, key, value);
      }
    },
    getFormattedPluginConfiguration(expectedImportConfig) {
      const pluginConfiguration = {
        importConfigurations: [],
        fieldMapping: this.fieldMapping,
        credentials: {},
      };

      for (const config of expectedImportConfig) {
        const result = this.formatConfig(config);

        pluginConfiguration.importConfigurations.push(result.configuration);
        pluginConfiguration.credentials[result.configuration.key] = result.credentials;
      }

      return pluginConfiguration;
    },
    async installPlugin(expectedImportConfig, returnResponse) {
      const pluginConfiguration = this.getFormattedPluginConfiguration(expectedImportConfig);

      let response = null;
      if (this.installed) {
        response = await this.$rest.plugins
          .put_resource(this.id, {
            isActive: true,
            pluginConfiguration,
          })
          .catch(error => {
            let error_message = null;
            let errors_list = "";
            if (error.response?.data?.errors?.length) {
              error.response.data.errors.forEach(item => {
                errors_list += (item.error + "\r \n");
              });
              error_message = errors_list;
            } else if (error.response?.data?.message) {
              error_message = error.response?.data?.message;
            }

            this.$store.commit('snackbar/showMessage', {
              content: error_message || 'Failed when trying to configure plugin',
              color: 'error',
            });
          });
      } else {
        response = await this.$rest.plugins
          .post_resource({
            plugin: +this.id,
            isActive: true,
            pluginConfiguration,
          })
          .then((resp) => {
            const selected_account = this.$store.getters["user/account"];
            this.$store.commit("user/update_selected_account", {
              ...selected_account,
              activePlugins: [...selected_account.activePlugins, resp.data],
            });

            return resp;
          })
          .catch(() => {
            this.$store.commit('snackbar/showMessage', {
              content: 'Failed when trying to install plugin',
              color: 'error',
            });
          });
      }

      if (returnResponse) {
        return response;
      }
      if (!response?.data) {
        return;
      }

      this.$store.commit('snackbar/showMessage', {
        content: this.installed
          ? 'Plugin configured successfully'
          : 'Plugin installed successfully',
        color: 'success',
      });

      await this.$router.push({ name: 'Plugins' });
    }
  },
};
</script>

<style scoped>
  .section-header {
    margin-top: 30px;
    font-size: 1.25rem;
    font-weight: bold;
  }
</style>
